import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../auth';

interface SecureVideoPlayerProps {
  recordingId: string;
  label?: string | null;
  hasVideo: boolean;
  contentType?: string | null;
  status?: string | null;
  durationSeconds?: number | null;
  startedAt?: string | null;
  className?: string;
  showDetailsFooter?: boolean;
  allowDownload?: boolean;
  showDownloadButton?: boolean;
  showPreview?: boolean;
  playButtonPlacement?: 'center' | 'bottom-left';
  aspectClassName?: string;
  mediaFit?: 'cover' | 'contain';
  dimPreview?: boolean;
  openFullscreenOnMobilePlay?: boolean;
}

interface MobileFullscreenVideoElement extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
  webkitSupportsFullscreen?: boolean;
}

interface FullscreenContainerElement extends HTMLDivElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

function canBrowserPlay(contentType: string | null | undefined) {
  if (!contentType) return 'probably';
  if (typeof document === 'undefined') return 'maybe';

  const video = document.createElement('video');
  return video.canPlayType(contentType);
}

function isMobilePlaybackDevice() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

  const userAgentData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (userAgentData?.mobile === true) return true;

  return window.matchMedia?.('(max-width: 767px)').matches
    || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export function SecureVideoPlayer({
  recordingId,
  label,
  hasVideo,
  contentType,
  status,
  durationSeconds,
  startedAt,
  className = '',
  showDetailsFooter = true,
  allowDownload = false,
  showDownloadButton = true,
  showPreview = allowDownload,
  playButtonPlacement = 'center',
  aspectClassName = 'aspect-video',
  mediaFit = 'cover',
  dimPreview = true,
  openFullscreenOnMobilePlay = false,
}: SecureVideoPlayerProps) {
  const { getToken, isSignedIn } = useAuth();
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlExpiry, setUrlExpiry] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<FullscreenContainerElement>(null);
  const pendingAutoplayRef = useRef(false);

  const logContext = () => ({
    recordingId,
    hasVideo,
    contentType,
    status,
    canPlayContentType: canBrowserPlay(contentType),
    isSignedIn,
    hasPlaybackUrl: Boolean(playbackUrl),
    hasPreviewUrl: Boolean(previewUrl),
    loading,
    error,
    urlExpiry,
  });

  useEffect(() => {
    console.info('[SecureVideoPlayer] state changed', logContext());
  }, [recordingId, hasVideo, contentType, status, isSignedIn, playbackUrl, previewUrl, loading, error, urlExpiry]);

  // Clear URL when it expires
  useEffect(() => {
    if (!urlExpiry) return;

    const timeout = setTimeout(() => {
      console.info('[SecureVideoPlayer] signed playback URL expired; clearing player source', {
        recordingId,
        urlExpiry,
      });
      setPlaybackUrl(null);
      setPreviewUrl(null);
      setUrlExpiry(null);
    }, urlExpiry * 1000);

    return () => clearTimeout(timeout);
  }, [urlExpiry]);

  const requestPlaybackUrl = async ({ showErrors }: { showErrors: boolean }) => {
    if (!isSignedIn) {
      if (showErrors) {
        console.warn('[SecureVideoPlayer] playback blocked because the user is signed out', {
          recordingId,
        });
        setError('Sign in to view recordings');
      }
      return null;
    }

    if (canBrowserPlay(contentType) === '') {
      if (showErrors) {
        console.warn('[SecureVideoPlayer] playback blocked because this browser cannot play the source format', {
          recordingId,
          contentType,
        });
        setError('Preparing playback for this browser');
      }
      return null;
    }

    const token = await getToken();
    console.info('[SecureVideoPlayer] auth token resolved for playback request', {
      recordingId,
      hasToken: Boolean(token),
    });

    const response = await fetch(`/api/recordings/id/${recordingId}/playback`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    console.info('[SecureVideoPlayer] playback response received', {
      recordingId,
      status: response.status,
      ok: response.ok,
      contentType: response.headers.get('content-type'),
    });

    if (!response.ok) {
      if (showErrors) {
        if (response.status === 401) {
          setError('Sign in to view recordings');
        } else if (response.status === 404) {
          setError('Recording not available');
        } else {
          setError('Failed to load video');
        }
      }
      return null;
    }

    const data = await response.json();
    console.info('[SecureVideoPlayer] playback payload parsed', {
      recordingId,
      hasPlaybackUrl: Boolean(data.playbackUrl),
      contentType: data.contentType,
      canPlayContentType: canBrowserPlay(data.contentType),
      expiresIn: data.expiresIn,
    });

    if (canBrowserPlay(data.contentType) === '') {
      if (showErrors) {
        console.warn('[SecureVideoPlayer] signed source is not playable in this browser', {
          recordingId,
          contentType: data.contentType,
        });
        setError('Preparing playback for this browser');
      }
      return null;
    }

    if (!data.playbackUrl) {
      if (showErrors) {
        console.warn('[SecureVideoPlayer] playback payload did not include a playbackUrl', {
          recordingId,
          payload: data,
        });
        setError('Recording not available');
      }
      return null;
    }

    setUrlExpiry(data.expiresIn);
    return data.playbackUrl as string;
  };

  const playLoadedVideo = useCallback(async (video: HTMLVideoElement, reason: string) => {
    if (!pendingAutoplayRef.current || !playbackUrl) return;

    try {
      await video.play();
      pendingAutoplayRef.current = false;
      console.info('[SecureVideoPlayer] native video autoplay started after overlay click', {
        recordingId,
        reason,
        readyState: video.readyState,
      });
    } catch (err) {
      const errorName = err instanceof DOMException ? err.name : null;
      console.warn('[SecureVideoPlayer] native video autoplay after overlay click failed', {
        recordingId,
        reason,
        error: err instanceof Error ? err.message : String(err),
        errorName,
        readyState: video.readyState,
      });

      if (errorName === 'NotAllowedError' || errorName === 'NotSupportedError') {
        pendingAutoplayRef.current = false;
      }
    }
  }, [playbackUrl, recordingId]);

  useEffect(() => {
    if (!playbackUrl || !videoRef.current) return;
    void playLoadedVideo(videoRef.current, 'playback-url-ready');
  }, [playbackUrl, playLoadedVideo]);

  const fetchPlaybackUrl = async () => {
    console.info('[SecureVideoPlayer] play button clicked', logContext());
    pendingAutoplayRef.current = true;

    const openedNativeFullscreen = openMobileFullscreenForPlay();
    if (openedNativeFullscreen) return;

    if (previewUrl) {
      setPlaybackUrl(previewUrl);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nextPlaybackUrl = await requestPlaybackUrl({ showErrors: true });
      if (nextPlaybackUrl) {
        setPlaybackUrl(nextPlaybackUrl);
        setPreviewUrl(nextPlaybackUrl);
      } else {
        pendingAutoplayRef.current = false;
      }
    } catch (err) {
      console.error('Error fetching playback URL:', err);
      setError('Failed to load video');
      pendingAutoplayRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showPreview || !hasVideo || !isSignedIn || previewUrl || playbackUrl) return;
    if (canBrowserPlay(contentType) === '') return;

    let cancelled = false;
    const loadPreviewFrame = async () => {
      try {
        const nextPreviewUrl = await requestPlaybackUrl({ showErrors: false });
        if (!cancelled && nextPreviewUrl) {
          setPreviewUrl(nextPreviewUrl);
        }
      } catch (err) {
        console.info('[SecureVideoPlayer] preview frame unavailable', {
          recordingId,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    };

    void loadPreviewFrame();
    return () => {
      cancelled = true;
    };
  }, [showPreview, hasVideo, isSignedIn, previewUrl, playbackUrl, contentType, recordingId]);

  const downloadRecording = async () => {
    if (!isSignedIn) {
      setError('Sign in to download recordings');
      return;
    }

    setDownloading(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/recordings/id/${recordingId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Recording not available for download');
      }

      const data = await response.json();
      if (!data.downloadUrl) {
        throw new Error('Recording not available for download');
      }

      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = data.filename || 'session-recording.mp4';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading recording:', err);
      alert(err instanceof Error ? err.message : 'Failed to download recording');
    } finally {
      setDownloading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  const mediaFitClassName = mediaFit === 'cover' ? 'object-cover' : 'object-contain';

  const openMobileFullscreenForPlay = () => {
    if (!openFullscreenOnMobilePlay || !isMobilePlaybackDevice()) return false;

    const previewVideo = previewVideoRef.current as MobileFullscreenVideoElement | null;
    if (previewVideo?.webkitEnterFullscreen) {
      try {
        previewVideo.controls = true;
        previewVideo.muted = false;
        previewVideo.currentTime = 0;
        void previewVideo.play().catch((error) => {
          console.warn('[SecureVideoPlayer] mobile native fullscreen preview play failed', {
            recordingId,
            error: error instanceof Error ? error.message : String(error),
          });
        });
        previewVideo.webkitEnterFullscreen();

        const resetPreviewVideo = () => {
          previewVideo.controls = false;
          previewVideo.muted = true;
          previewVideo.removeEventListener('webkitendfullscreen', resetPreviewVideo);
        };
        previewVideo.addEventListener('webkitendfullscreen', resetPreviewVideo);

        console.info('[SecureVideoPlayer] opened mobile native video fullscreen from preview', {
          recordingId,
        });
        pendingAutoplayRef.current = false;
        return true;
      } catch (error) {
        console.warn('[SecureVideoPlayer] mobile native video fullscreen failed', {
          recordingId,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    const fullscreenContainer = containerRef.current;
    const requestFullscreen = fullscreenContainer?.requestFullscreen ?? fullscreenContainer?.webkitRequestFullscreen;
    if (requestFullscreen && fullscreenContainer) {
      const fullscreenResult = requestFullscreen.call(fullscreenContainer);
      if (fullscreenResult && typeof fullscreenResult.catch === 'function') {
        void fullscreenResult.catch((error: unknown) => {
          console.warn('[SecureVideoPlayer] mobile player fullscreen request failed', {
            recordingId,
            error: error instanceof Error ? error.message : String(error),
          });
        });
      }
    }

    return false;
  };

  if (!hasVideo) {
    const message = status === 'failed' ? 'Recording failed' : 'Recording processing...';

    return (
      <div className={`bg-bg-secondary rounded-lg overflow-hidden ${className}`}>
        <div className={`w-full ${aspectClassName} bg-panel2 flex items-center justify-center text-text-dim text-sm`}>
          {message}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`bg-bg-secondary rounded-lg overflow-hidden ${className}`}>
      {playbackUrl ? (
        <div className={`relative bg-black ${aspectClassName}`}>
          <video
            ref={videoRef}
            src={playbackUrl}
            controls
            autoPlay
            playsInline
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            disableRemotePlayback
            onContextMenu={(e) => e.preventDefault()}
            onLoadedMetadata={(event) => {
              console.info('[SecureVideoPlayer] video metadata loaded', {
                recordingId,
                duration: event.currentTarget.duration,
                videoWidth: event.currentTarget.videoWidth,
                videoHeight: event.currentTarget.videoHeight,
                readyState: event.currentTarget.readyState,
              });
              void playLoadedVideo(event.currentTarget, 'metadata-loaded');
            }}
            onCanPlay={(event) => {
              console.info('[SecureVideoPlayer] video can play', {
                recordingId,
                readyState: event.currentTarget.readyState,
                networkState: event.currentTarget.networkState,
              });
              void playLoadedVideo(event.currentTarget, 'can-play');
            }}
            onPlay={(event) => {
              pendingAutoplayRef.current = false;
              console.info('[SecureVideoPlayer] native video playback started', {
                recordingId,
                currentTime: event.currentTarget.currentTime,
                readyState: event.currentTarget.readyState,
              });
            }}
            onPause={(event) => {
              console.info('[SecureVideoPlayer] native video playback paused', {
                recordingId,
                currentTime: event.currentTarget.currentTime,
                readyState: event.currentTarget.readyState,
              });
            }}
            onError={(event) => {
              const mediaError = event.currentTarget.error;
              console.error('[SecureVideoPlayer] native video element error', {
                recordingId,
                code: mediaError?.code,
                message: mediaError?.message,
                networkState: event.currentTarget.networkState,
                readyState: event.currentTarget.readyState,
                currentSrc: event.currentTarget.currentSrc,
              });
              setError('This recording could not be decoded for playback');
              setPlaybackUrl(null);
              setUrlExpiry(null);
            }}
            className={`absolute inset-0 h-full w-full bg-black ${mediaFitClassName}`}
            preload="metadata"
          />
        </div>
      ) : (
        <button
          type="button"
          aria-label="Play video"
          onClick={fetchPlaybackUrl}
          disabled={loading}
          className={`relative w-full ${aspectClassName} bg-panel2 flex flex-col items-center justify-center text-text-dim hover:bg-panel2/80 transition-colors cursor-pointer group overflow-hidden`}
        >
          {previewUrl && !error && (
            <>
              <video
                ref={previewVideoRef}
                src={previewUrl}
                muted
                playsInline
                preload="auto"
                onLoadedMetadata={(event) => {
                  const previewVideo = event.currentTarget;
                  if (Number.isFinite(previewVideo.duration) && previewVideo.duration > 0.25) {
                    previewVideo.currentTime = 0.1;
                  }
                }}
                className={`absolute inset-0 h-full w-full ${mediaFitClassName} opacity-90`}
              />
              {dimPreview && <span className="absolute inset-0 bg-black/45" aria-hidden="true" />}
            </>
          )}
          {loading ? (
            <div className="relative z-10 flex items-center gap-2 text-white">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm">Loading...</span>
            </div>
          ) : error ? (
            <div className="relative z-10 text-center px-4">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto mb-2 text-warn">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span className="text-sm text-warn">{error}</span>
              {error === 'Preparing playback for this browser' && (
                <p className="text-xs mt-1 text-text-faint">
                  The source recording is saved, but this browser needs a compatible playback version.
                </p>
              )}
              {!isSignedIn && (
                <p className="text-xs mt-1 text-text-faint">
                  Only session participants can view recordings
                </p>
              )}
            </div>
          ) : (
            playButtonPlacement === 'bottom-left' ? (
              <>
                <span className="absolute bottom-4 left-4 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-black text-white shadow-lg ring-1 ring-white/20 transition-transform group-hover:scale-105 group-hover:bg-black/90">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="ml-0.5" aria-hidden="true">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </span>
                {!isSignedIn && (
                  <span className="absolute bottom-5 left-[76px] z-10 rounded-full bg-black/70 px-3 py-1 text-xs text-white shadow-lg">
                    Sign in required
                  </span>
                )}
              </>
            ) : (
              <>
                <div className="relative z-10 w-20 h-20 rounded-full bg-white text-text flex items-center justify-center group-hover:scale-105 group-hover:bg-white group-hover:ring-white/45 transition-all mb-3 shadow-2xl ring-4 ring-white/25">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor" className="ml-1">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </div>
                <span className="relative z-10 rounded-full bg-black/70 px-3 py-1 text-sm font-medium text-white shadow-lg">
                  Click to play
                </span>
                {!isSignedIn && (
                  <span className="relative z-10 text-xs text-text-faint mt-1">Sign in required</span>
                )}
              </>
            )
          )}
        </button>
      )}

      {allowDownload && showDownloadButton && hasVideo && (
        <div className="p-2.5 flex justify-end border-t border-line-soft">
          <button
            type="button"
            onClick={downloadRecording}
            disabled={downloading}
            className="btn btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading ? 'Preparing...' : 'Download'}
          </button>
        </div>
      )}

      {showDetailsFooter && (
        <div className="p-2.5 flex items-center justify-between gap-3 text-xs border-t border-line-soft">
          <div className="min-w-0">
            {label && (
              <div className="truncate font-medium text-text">{label}</div>
            )}
            <div className="text-text-dim">
              {startedAt ? formatDate(startedAt) : 'Date unknown'}
            </div>
          </div>
          {durationSeconds && (
            <span className="shrink-0 text-text-dim">
              {formatDuration(durationSeconds)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
